import { useEffect, useState } from 'react';

import { VehicleListFiltersViewModel } from '@api/index';
import { ListingFilterItemKey } from '@components/ui/filters/MultiSelectionFilter/types';
import { getSelectedFilterByKeyNameFromQuery } from '@utils/carRentalFilterUtils';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { CAR_RENTAL_FILTER_QUERY_PARAM_KEYS } from '@page/carRental/list/constants';

import { getPriceRange } from './utils';

export interface CarRentalListFilterProps {
  filters: VehicleListFiltersViewModel[];
  prices: number[];
  setPrices: (value: number[]) => void;
  fuelTypes: ListingFilterItemKey[];
  setFuelTypes: (value: ListingFilterItemKey[]) => void;
  transmissionTypes: ListingFilterItemKey[];
  setTransmissionTypes: (value: ListingFilterItemKey[]) => void;
  vehicleClasses: ListingFilterItemKey[];
  setVehicleClasses: (value: ListingFilterItemKey[]) => void;
  providers: ListingFilterItemKey[];
  setProviders: (value: ListingFilterItemKey[]) => void;
  doorCounts: ListingFilterItemKey[];
  setDoorCounts: (value: ListingFilterItemKey[]) => void;
  seatCounts: ListingFilterItemKey[];
  setSeatCounts: (value: ListingFilterItemKey[]) => void;
  luggageCounts: ListingFilterItemKey[];
  setLuggageCounts: (value: ListingFilterItemKey[]) => void;
}

export const useCarRentalFilters = (filters: VehicleListFiltersViewModel[]) => {
  const query = useSearchParamsToQuery();
  const { priceRanges } = getPriceRange(filters);

  const [prices, setPrices] = useState<number[]>(
    priceRanges?.[0] && priceRanges?.[1]
      ? [priceRanges[0], priceRanges?.[1]]
      : []
  );

  const [fuelTypes, setFuelTypes] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES
    )
  );

  const [transmissionTypes, setTransmissionTypes] = useState<
    ListingFilterItemKey[]
  >(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
    )
  );

  const [vehicleClasses, setVehicleClasses] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
    )
  );

  const [providers, setProviders] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS
    )
  );

  const [doorCounts, setDoorCounts] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS
    )
  );

  const [seatCounts, setSeatCounts] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS
    )
  );

  const [luggageCounts, setLuggageCounts] = useState<ListingFilterItemKey[]>(
    getSelectedFilterByKeyNameFromQuery(
      query,
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
    )
  );

  useEffect(() => {
    if (
      !query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MIN] &&
      !query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MAX]
    ) {
      const { priceRanges } = getPriceRange(filters);
      setPrices(
        priceRanges?.[0] && priceRanges?.[1]
          ? [priceRanges[0], priceRanges?.[1]]
          : []
      );
    }
  }, [filters, query]);

  return {
    prices,
    setPrices,
    fuelTypes,
    setFuelTypes,
    transmissionTypes,
    setTransmissionTypes,
    vehicleClasses,
    setVehicleClasses,
    providers,
    setProviders,
    doorCounts,
    setDoorCounts,
    seatCounts,
    setSeatCounts,
    luggageCounts,
    setLuggageCounts,
  };
};
